// src/components/staff/CalendarSelect.tsx
import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { fetchCalendars, fetchStaff, Staff, CalendarSummary } from "../../api/staff";

interface StaffCalendarSelectProps {
  staffId?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
}

const StaffCalendarSelect: React.FC<StaffCalendarSelectProps> = ({
  staffId,
  value,
  onChange,
  label = "Calendar",
}) => {
  const [calendars, setCalendars] = useState<CalendarSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [rawCalendars, rawStaff] = await Promise.all([
          fetchCalendars(),
          fetchStaff(),
        ]);

        // ⭐ Guarantee arrays
        const allCalendars = Array.isArray(rawCalendars) ? rawCalendars : [];
        const staffList = Array.isArray(rawStaff) ? rawStaff : [];

        // Build calendar → staff assignments
        const assignments = new Map<string, string>();
        staffList.forEach((s: Staff) => {
          if (s.calendar_id) {
            assignments.set(s.calendar_id, s.staff_id);
          }
        });

        // Filter calendars
        const filtered = allCalendars.filter((cal) => {
          const assignedTo = assignments.get(cal.id);

          // Assigned to someone else → hide
          if (assignedTo && assignedTo !== staffId) return false;

          // Disabled and not ours → hide
          if (!cal.is_bookable && assignedTo !== staffId) return false;

          return true;
        });

        setCalendars(filtered);
      } catch (err: any) {
        console.error("🚨 Failed to load calendars/staff:", err);
        setError(err.message || "Failed to load calendars.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [staffId]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    const val = e.target.value;
    onChange(val === "" ? null : val);
  };

  const currentCalendar = calendars.find((c) => c.id === value);

  return (
    <FormControl fullWidth size="small" error={!!error}>
      <InputLabel id="staff-calendar-label">{label}</InputLabel>

      <Select
        labelId="staff-calendar-label"
        label={label}
        value={value || ""}
        onChange={handleChange}
        disabled={loading}
      >
        <MenuItem value="">
          <em>Not assigned</em>
        </MenuItem>

        {calendars.map((cal) => (
          <MenuItem key={cal.id} value={cal.id}>
            {cal.display_name}
            {!cal.is_bookable && " (Disabled in Settings)"}
          </MenuItem>
        ))}
      </Select>

      {loading && (
        <FormHelperText>
          <CircularProgress size={14} sx={{ mr: 1 }} /> Loading calendars…
        </FormHelperText>
      )}

      {error && <FormHelperText>{error}</FormHelperText>}

      {!error && currentCalendar && !currentCalendar.is_bookable && (
        <FormHelperText>
          This calendar is disabled in Settings, so this staff member will not
          appear on the booking page until it’s re-enabled.
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default StaffCalendarSelect;
